import React, { useState } from "react";
import { Link, withRouter } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Checkbox,
  Confirm,
  Loader,
  Table,
  Header,
  Icon,
  Form,
  List,
  Label,
  Popup,
} from "semantic-ui-react";
import { ADMIN_GET_STUDENT, ADMIN_PUT_STUDENT } from "../../GraphQL";
import { useQuery, useMutation } from "@apollo/client";
import UserSearch from "./UserSearch";

const getStudentInput = (student) => {
  return {
    id: student.id,
    parents: student.parents.map((p) => p.sub),
    teacher: student.myTeacher.sub,
    archived: student.archived,
  };
};

const ItemLabel = ({ label, removable, onRemove, ...props }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  return (
    <>
      <Label as="a" {...props} onClick={() => setConfirmOpen(true)}>
        {label} {removable ? <Icon name="delete" /> : null}
      </Label>
      <Confirm
        open={confirmOpen}
        content={`Are you sure you want to remove ${label}?`}
        confirmButton="Remove"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onRemove}
      />
    </>
  );
};

const StudentTable = ({ initialStudent, editing, onSubmit, onCancel }) => {
  const [student, setStudent] = useState(initialStudent);
  return (
    <Form>
      <Table definition>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Name</Table.Cell>
            <Table.Cell>{student.name}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>ID</Table.Cell>
            <Table.Cell>
              {student.id}{" "}
              <Popup
                position="top center"
                content="Copied!"
                mouseLeaveDelay={500}
                on="click"
                trigger={
                  <Icon
                    onClick={() => navigator.clipboard.writeText(student.id)}
                    name="copy"
                  />
                }
              />
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Active</Table.Cell>
            <Table.Cell>
              <Checkbox
                toggle
                disabled={!editing}
                checked={!student.archived}
                onChange={(e, data) =>
                  setStudent({ ...student, archived: !data.checked })
                }
              />
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Parents</Table.Cell>
            <Table.Cell>
              <List>
                {student.parents
                  .map(
                    (p, i) =>
                      p ?? {
                        sub: null,
                        name: "Unknown User",
                      }
                  )
                  .map((p, i) => (
                    <List.Item key={p.sub ?? "null"}>
                      {editing ? (
                        <ItemLabel
                          size="medium"
                          label={p.name}
                          key={p.sub}
                          onRemove={() =>
                            setStudent({
                              ...student,
                              parents: student.parents.filter(
                                (u) => u && u.sub !== p.sub
                              ),
                            })
                          }
                          removable
                        />
                      ) : (
                        <a href={`/admin/users/${p.sub}`}>{p.name}</a>
                      )}
                    </List.Item>
                  ))}
              </List>
              {editing ? (
                <UserSearch
                  onSelect={(newParent) => {
                    console.log(newParent);
                    setStudent({
                      ...student,
                      parents: [...student.parents, newParent],
                    });
                  }}
                />
              ) : null}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Teacher</Table.Cell>
            <Table.Cell>
              <List>
                <List.Item key={student.myTeacher.sub}>
                  {editing ? (
                    <UserSearch
                      initialUser={student.myTeacher}
                      onSelect={(newTeacher) =>
                        setStudent({
                          ...student,
                          myTeacher: newTeacher,
                        })
                      }
                    />
                  ) : (
                    <a href={`/admin/users/${student.myTeacher.sub}`}>
                      {student.myTeacher.name}
                    </a>
                  )}
                </List.Item>
              </List>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      {editing ? (
        <>
          <Button
            floated="right"
            onClick={() => {
              setStudent(initialStudent);
              onCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            floated="right"
            color="teal"
            onClick={() => onSubmit(student)}
          >
            Save
          </Button>
        </>
      ) : null}
    </Form>
  );
};

const StudentAdminPage = ({ match }) => {
  const [editing, setEditing] = useState(false);
  const { loading, error, data } = useQuery(ADMIN_GET_STUDENT, {
    variables: { id: match.params.id },
  });

  const [adminPutStudent] = useMutation(ADMIN_PUT_STUDENT);

  if (loading) {
    return <Loader active />;
  } else if (error) {
    throw error;
  }

  const student = data.getStudent;

  if (!student) {
    return (
      <div>
        <Breadcrumb size="tiny">
          <Breadcrumb.Section>
            <Link to="/admin">Admin Home</Link>
          </Breadcrumb.Section>
          <Breadcrumb.Divider icon="right angle" />
          <Breadcrumb.Section active>{match.params.id}</Breadcrumb.Section>
        </Breadcrumb>
        <Header as="h1">Student Not Found</Header>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb size="tiny">
        <Breadcrumb.Section>
          <Link to="/admin">Admin Home</Link>
        </Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>{student.name}</Breadcrumb.Section>
      </Breadcrumb>

      <Button
        floated="right"
        onClick={() => setEditing(true)}
        disabled={editing}
      >
        Edit
      </Button>

      <Header as="h1">
        {student.name}
        <Label color={student.archived ? "grey" : "blue"} horizontal>
          {student.archived ? "Archived" : "Active"}
        </Label>
      </Header>

      <StudentTable
        initialStudent={student}
        editing={editing}
        onSubmit={(newStudent) => {
          setEditing(false);
          const params = getStudentInput(newStudent);
          //          alert(JSON.stringify(params, undefined, 2));
          adminPutStudent({ variables: { input: params } });
        }}
        onCancel={() => setEditing(false)}
      />
    </div>
  );
};

export default withRouter(StudentAdminPage);
